import { useState } from 'react'
import { Blocks, Loader2, Pencil, Plus, Search, Trash2 } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useDebounce } from '@/components/hooks/use-debounce'
import {
  useAddOnCatalog,
  useCreateAddOnCatalogItem,
  useDeleteAddOnCatalogItem,
  useUpdateAddOnCatalogItem,
} from '@/features/customers/addon-hooks'
import { formatMoney } from '@/lib/format'
import { BOAT_TYPES, BOAT_TYPE_LABELS, type BoatType } from '@/types/inventory'
import type { AddOnCatalogItem, AddOnCatalogWriteBody } from '@/types/addon'

/**
 * AddOnsCatalogTab manages the add-on price book: the purchasable extras
 * (accessories, services, F&I) sales can attach to deals. Rows carry dealer
 * cost + customer price (+ install) and the hull types they fit.
 */
export function AddOnsCatalogTab({
  q,
  onQChange,
}: {
  q?: string
  onQChange?: (q: string) => void
}) {
  const debounced = useDebounce(q ?? '', 300)
  // Always an object: undefined would disable the query (the picker uses
  // that to stay idle while its dialog is closed).
  const { data: items = [], isLoading } = useAddOnCatalog({ q: debounced || undefined })
  const createMutation = useCreateAddOnCatalogItem()
  const updateMutation = useUpdateAddOnCatalogItem()
  const deleteMutation = useDeleteAddOnCatalogItem()

  const [editing, setEditing] = useState<AddOnCatalogItem | null>(null)
  const [creating, setCreating] = useState(false)
  const [deleting, setDeleting] = useState<AddOnCatalogItem | null>(null)

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div className="space-y-1.5">
          <CardTitle className="flex items-center gap-2 text-base">
            <Blocks className="h-4 w-4" /> Add-On Price Book
          </CardTitle>
          <CardDescription>
            Extras sales can attach to a deal — accessories, services, and F&amp;I. Prices here
            default onto the deal's lines.
          </CardDescription>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="absolute top-2.5 left-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              className="w-56 pl-8"
              placeholder="Search name or SKU…"
              value={q ?? ''}
              onChange={(e) => onQChange?.(e.target.value)}
            />
          </div>
          <Button onClick={() => setCreating(true)}>
            <Plus className="mr-1.5 h-3.5 w-3.5" /> New Item
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : items.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">
            No add-on catalog items yet.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>SKU</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">Cost</TableHead>
                <TableHead className="text-right">Price</TableHead>
                <TableHead className="text-right">Install</TableHead>
                <TableHead>Fits</TableHead>
                <TableHead className="w-20" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="font-mono text-xs">{item.sku || '—'}</TableCell>
                  <TableCell>
                    <span className="font-medium">{item.name}</span>
                    {!item.active && (
                      <Badge variant="secondary" className="ml-2 text-[10px]">
                        inactive
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="text-xs text-muted-foreground">{item.category}</span>
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">
                    {formatMoney(item.cost)}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatMoney(item.sell_price)}
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">
                    {item.install_price > 0 ? formatMoney(item.install_price) : '—'}
                  </TableCell>
                  <TableCell>
                    {item.applicable_boat_types.length === 0 ? (
                      <span className="text-xs text-muted-foreground">All hulls</span>
                    ) : (
                      <span className="text-xs">
                        {item.applicable_boat_types
                          .map((t) => BOAT_TYPE_LABELS[t as BoatType] ?? t)
                          .join(', ')}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        aria-label={`Edit ${item.name}`}
                        onClick={() => setEditing(item)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive"
                        aria-label={`Delete ${item.name}`}
                        onClick={() => setDeleting(item)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <AddOnCatalogItemDialog
          open={creating || !!editing}
          item={editing}
          onOpenChange={(o) => {
            if (!o) {
              setCreating(false)
              setEditing(null)
            }
          }}
          isPending={createMutation.isPending || updateMutation.isPending}
          onSubmit={(body) => {
            if (editing) {
              updateMutation.mutate({ id: editing.id, body }, { onSuccess: () => setEditing(null) })
            } else {
              createMutation.mutate(body, { onSuccess: () => setCreating(false) })
            }
          }}
        />

        <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>Delete add-on item?</DialogTitle>
              <DialogDescription>
                “{deleting?.name}” leaves the price book. Lines already attached to deals keep their
                snapshot prices.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleting(null)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={deleteMutation.isPending}
                onClick={() => {
                  if (!deleting) return
                  deleteMutation.mutate(deleting.id, { onSettled: () => setDeleting(null) })
                }}
              >
                {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Delete Item
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  )
}

// --- Create / edit dialog ---

function AddOnCatalogItemDialog({
  open,
  item,
  onOpenChange,
  onSubmit,
  isPending,
}: {
  open: boolean
  item: AddOnCatalogItem | null
  onOpenChange: (open: boolean) => void
  onSubmit: (body: AddOnCatalogWriteBody) => void
  isPending: boolean
}) {
  const [form, setForm] = useState<AddOnCatalogWriteBody>(emptyForm)

  // Re-seed the form whenever the dialog target changes ("adjust state during
  // render", like the deal edit dialog).
  const formKey = `${open}:${item?.id ?? 'new'}`
  const [prevKey, setPrevKey] = useState(formKey)
  if (prevKey !== formKey) {
    setPrevKey(formKey)
    setForm(
      item
        ? {
            sku: item.sku,
            name: item.name,
            description: item.description,
            category: item.category,
            cost: item.cost,
            sell_price: item.sell_price,
            install_price: item.install_price,
            applicable_boat_types: item.applicable_boat_types,
            active: item.active,
          }
        : emptyForm,
    )
  }

  const toggleType = (t: BoatType) => {
    setForm((f) => {
      const current = f.applicable_boat_types ?? []
      return {
        ...f,
        applicable_boat_types: current.includes(t)
          ? current.filter((x) => x !== t)
          : [...current, t],
      }
    })
  }

  const isValid = (form.name ?? '').trim().length > 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{item ? 'Edit add-on item' : 'New add-on item'}</DialogTitle>
          <DialogDescription>
            Cost is the dealer's own price; sell + install is what the customer pays when the item
            lands on a deal.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3">
          <div className="grid grid-cols-3 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="addon-sku">SKU</Label>
              <Input
                id="addon-sku"
                value={form.sku ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, sku: e.target.value }))}
                placeholder="Optional"
              />
            </div>
            <div className="col-span-2 grid gap-1.5">
              <Label htmlFor="addon-name">Name</Label>
              <Input
                id="addon-name"
                value={form.name ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Tower Speakers (Pair)"
              />
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="addon-desc">Description</Label>
            <Input
              id="addon-desc"
              value={form.description ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Shown on the deal's add-on lines"
            />
          </div>
          <div className="grid grid-cols-4 gap-3">
            <div className="col-span-2 grid gap-1.5">
              <Label>Category</Label>
              <Select
                value={form.category ?? 'Other'}
                onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="addon-cost">Cost ($)</Label>
              <Input
                id="addon-cost"
                type="number"
                min="0"
                step="0.01"
                value={form.cost ?? 0}
                onChange={(e) => setForm((f) => ({ ...f, cost: Number(e.target.value) || 0 }))}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="addon-sell">Sell ($)</Label>
              <Input
                id="addon-sell"
                type="number"
                min="0"
                step="0.01"
                value={form.sell_price ?? 0}
                onChange={(e) =>
                  setForm((f) => ({ ...f, sell_price: Number(e.target.value) || 0 }))
                }
              />
            </div>
          </div>
          <div className="grid grid-cols-4 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="addon-install">Install ($)</Label>
              <Input
                id="addon-install"
                type="number"
                min="0"
                step="0.01"
                value={form.install_price ?? 0}
                onChange={(e) =>
                  setForm((f) => ({ ...f, install_price: Number(e.target.value) || 0 }))
                }
              />
            </div>
            <div className="col-span-3 flex items-end">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={form.active ?? true}
                  onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                />
                Active (offerable on new deals)
              </label>
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label>Fits hull types</Label>
            <div className="flex flex-wrap gap-1.5">
              {BOAT_TYPES.map((t) => {
                const on = (form.applicable_boat_types ?? []).includes(t)
                return (
                  <Button
                    key={t}
                    type="button"
                    size="sm"
                    variant={on ? 'default' : 'outline'}
                    className="h-7 px-2.5 text-xs"
                    onClick={() => toggleType(t)}
                  >
                    {BOAT_TYPE_LABELS[t]}
                  </Button>
                )
              })}
            </div>
            <p className="text-xs text-muted-foreground">
              None selected means the item fits every hull.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!isValid || isPending} onClick={() => onSubmit(form)}>
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {item ? 'Save Changes' : 'Create Item'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

const CATEGORIES = [
  'Electronics',
  'Fishing',
  'Audio',
  'Watersports',
  'Pontoon',
  'Trailering',
  'Covers & Canvas',
  'Safety',
  'Power',
  'Services',
  'F&I',
  'Other',
]

const emptyForm: AddOnCatalogWriteBody = {
  sku: '',
  name: '',
  description: '',
  category: 'Other',
  cost: 0,
  sell_price: 0,
  install_price: 0,
  applicable_boat_types: [],
  active: true,
}
